import { useState } from 'react';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewListIcon from '@mui/icons-material/ViewList';
import { RelationshipGraph } from './RelationshipGraph';
import { useNavigate } from '@tanstack/react-router';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
  useEntityRelationships,
  useAddEntityRelationship,
  useDeleteEntityRelationship,
  type EntityRelationshipDto,
} from '../../api/queries/relationships';
import { useEntities, type EntitySummaryDto } from '../../api/queries/entities';

const RELATIONSHIP_TYPES = [
  'parent',
  'child',
  'ally',
  'enemy',
  'mentor',
  'sibling',
  'lover',
  'employer',
  'rival',
  'friend',
  'subordinate',
  'other',
] as const;

interface RelationshipsTabProps {
  entityId: string;
  entityName: string;
}

export function RelationshipsTab({
  entityId,
  entityName,
}: RelationshipsTabProps) {
  const relationships = useEntityRelationships(entityId);
  const deleteRel = useDeleteEntityRelationship(entityId);

  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] =
    useState<EntityRelationshipDto | null>(null);
  const [viewMode, setViewMode] = useState<'graph' | 'list'>('graph');

  const getOtherEntityName = (rel: EntityRelationshipDto): string => {
    if (rel.entityAId === entityId) {
      return rel.entityBName ?? rel.entityBId;
    }
    return rel.entityAName ?? rel.entityAId;
  };

  if (relationships.isLoading) return <CircularProgress />;
  if (relationships.isError)
    return <Typography>Failed to load relationships.</Typography>;

  const items = relationships.data ?? [];

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {items.length} relationship{items.length === 1 ? '' : 's'}
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => {
            if (v) setViewMode(v);
          }}
          size="small"
        >
          <ToggleButton value="graph" aria-label="graph view">
            <AccountTreeIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Relationship
        </Button>
      </Stack>

      {/* Graph view */}
      {viewMode === 'graph' && (
        <RelationshipGraph
          entityId={entityId}
          entityName={entityName}
          relationships={items}
          onNavigate={(id) =>
            navigate({ to: '/entities/$entityId', params: { entityId: id } })
          }
        />
      )}

      {/* List view */}
      {viewMode === 'list' && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No relationships defined yet. Add one to track how {entityName}{' '}
          relates to other entities.
        </Typography>
      )}

      {viewMode === 'list' &&
        items.map((rel) => (
          <Card key={rel.id} variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {getOtherEntityName(rel)}
                </Typography>
                <Chip
                  size="small"
                  label={rel.relationshipType}
                  color="primary"
                  variant="outlined"
                />
                <Tooltip title="Delete relationship">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget(rel)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              {rel.notes && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {rel.notes}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}

      <AddRelationshipDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        entityId={entityId}
        entityName={entityName}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete relationship"
        message={`Remove the "${deleteTarget?.relationshipType}" relationship with ${deleteTarget ? getOtherEntityName(deleteTarget) : ''}?`}
        onConfirm={async () => {
          if (deleteTarget) {
            await deleteRel.mutateAsync(deleteTarget.id);
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </Stack>
  );
}

function AddRelationshipDialog({
  open,
  onClose,
  entityId,
  entityName,
}: {
  open: boolean;
  onClose: () => void;
  entityId: string;
  entityName: string;
}) {
  const [search, setSearch] = useState('');
  const [selectedEntity, setSelectedEntity] = useState<EntitySummaryDto | null>(
    null,
  );
  const [relType, setRelType] = useState<string>('friend');
  const [notes, setNotes] = useState('');

  const entities = useEntities({
    search: search.trim() || undefined,
    pageSize: 20,
  });
  const addRel = useAddEntityRelationship(entityId);

  // Filter out the current entity from the options
  const options = (entities.data?.items ?? []).filter((e) => e.id !== entityId);

  const handleSubmit = async () => {
    if (!selectedEntity) return;
    await addRel.mutateAsync({
      targetEntityId: selectedEntity.id,
      relationshipType: relType,
      notes: notes.trim() || undefined,
    });
    setSelectedEntity(null);
    setRelType('friend');
    setNotes('');
    setSearch('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add relationship for {entityName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Autocomplete
            options={options}
            getOptionLabel={(opt) => opt.name}
            loading={entities.isLoading}
            value={selectedEntity}
            onChange={(_, val) => setSelectedEntity(val)}
            inputValue={search}
            onInputChange={(_, val) => setSearch(val)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">{option.name}</Typography>
                  <Chip size="small" label={option.entityType} />
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search entities"
                placeholder="Type to search..."
              />
            )}
          />

          <FilterableSelect
            options={RELATIONSHIP_TYPES.map((type) => ({
              value: type,
              label: type.charAt(0).toUpperCase() + type.slice(1),
            }))}
            value={relType}
            onChange={(newValue) => setRelType(newValue)}
            label="Relationship type"
            allowEmpty={false}
          />

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            placeholder="Any additional context about this relationship"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedEntity || addRel.isPending}
        >
          {addRel.isPending ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
